import React, { useContext, type JSX } from 'react';
import query from './EkonomiskAnalysKontodelarQuery.gql';
import {
  KontodelarForKodplanVariables as IVariables,
  KontodelarForKodplan as IData,
  KontodelarForKodplan_db_EKKontodel_All as IKontodelarResult,
  KontodelarForKodplan_db_EKKontoplan_All__Associations_Kontodelsdef as IKontoPlanDef,
} from './_QueryTypes/KontodelarForKodplan';
import mergeSort, {
  stringCompareNumeric,
} from '../../../../../../../Utils/mergeSort';
import { KodplanContext } from '../../../../../../../Components/KodplanManager/Context/KodplanContext';
import Logger from '../../../../../../../Utils/Logger';
import { getActiveKoddelar } from '../../../../../../../Components/KodplanManager/Utils/koddelarUtils';
import { EkonomiskAnalysSideMenuContext } from '../../Components/SideMenu/Context/EkonomiskAnalysSideMenuContext';
import { datePartsToCompletedDateString } from '../../../../../../../Utils/dateUtils';
import { getTodaysDate } from '../../../../../../../Utils/utils';
import useHandledQuery from '../../../../../../../Components/QueryComponents/HandledQuery/HandledQueryHook';
import { ActiveDatabaseContext } from '../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { EkonomiskAnalysContext } from '../../Components/Context/EkonomiskAnalysContext';
import { filterKoddelar } from '../../Components/SideMenu/Components/UrvalOchSummering/Components/Urval/Utils/UrvalUtils';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';

interface IProps {
  loadingComponent?: JSX.Element | null;
  children: (result: IKontodelar) => React.ReactElement<any>;
}

type KontodelarKeys =
  | 'KONTODEL_1'
  | 'KONTODEL_2'
  | 'KONTODEL_3'
  | 'KONTODEL_4'
  | 'KONTODEL_5'
  | 'KONTODEL_6'
  | 'KONTODEL_7'
  | 'KONTODEL_8'
  | 'KONTODEL_9'
  | 'KONTODEL_10';

export type IKontodelar = { [key in KontodelarKeys]?: IKontodel } & { [key: string]: IKontodel | undefined };

export interface IKontodel {
  kontodelar: IKontodelValue[];
  codeLength?: number;
}

export interface IKontodelValue {
  name: string;
  id: string;
}

const sortKontodelar = (kontoDelar: IKontodelar): IKontodelar => {
  let result: IKontodelar = {};
  for (let kontodel in kontoDelar) {
    if (
      Object.prototype.hasOwnProperty.call(!(kontoDelar as object), kontodel)
    ) {
      continue;
    }
    if (!result[kontodel]) {
      result[kontodel] = { kontodelar: [] };
    }
    const resultKontodel = result[kontodel]!;
    const kontoDelarKontodel = kontoDelar[kontodel];
    if (!kontoDelarKontodel) {
      continue;
    }
    resultKontodel.codeLength = kontoDelarKontodel.codeLength;
    resultKontodel.kontodelar = mergeSort(
      kontoDelarKontodel.kontodelar,
      (a: IKontodelValue, b: IKontodelValue) => {
        return stringCompareNumeric(a.id, b.id);
      }
    );
  }

  return result;
};

const getKontodelarValues = (
  rawKontodelar: IKontodelarResult[],
  kontoPlan: IKontoPlanDef[]
): IKontodelar => {
  const kontodelarMap: IKontodelar = {
    KONTODEL_1: {
      kontodelar: [],
      codeLength: kontoPlan[0] ? kontoPlan[0].ANTAL_POS : undefined,
    },
    KONTODEL_2: {
      kontodelar: [],
      codeLength: kontoPlan[1] ? kontoPlan[1].ANTAL_POS : undefined,
    },
    KONTODEL_3: {
      kontodelar: [],
      codeLength: kontoPlan[2] ? kontoPlan[2].ANTAL_POS : undefined,
    },
    KONTODEL_4: {
      kontodelar: [],
      codeLength: kontoPlan[3] ? kontoPlan[3].ANTAL_POS : undefined,
    },
    KONTODEL_5: {
      kontodelar: [],
      codeLength: kontoPlan[4] ? kontoPlan[4].ANTAL_POS : undefined,
    },
    KONTODEL_6: {
      kontodelar: [],
      codeLength: kontoPlan[5] ? kontoPlan[5].ANTAL_POS : undefined,
    },
    KONTODEL_7: {
      kontodelar: [],
      codeLength: kontoPlan[6] ? kontoPlan[6].ANTAL_POS : undefined,
    },
    KONTODEL_8: {
      kontodelar: [],
      codeLength: kontoPlan[7] ? kontoPlan[7].ANTAL_POS : undefined,
    },
    KONTODEL_9: {
      kontodelar: [],
      codeLength: kontoPlan[8] ? kontoPlan[8].ANTAL_POS : undefined,
    },
    KONTODEL_10: {
      kontodelar: [],
      codeLength: kontoPlan[9] ? kontoPlan[9].ANTAL_POS : undefined,
    },
  };

  rawKontodelar.forEach(rawKontodel => {
    const kontodelValue: IKontodelValue = {
      name: `${rawKontodel.KONTODEL} ${rawKontodel.BENAMNING}`,
      id: rawKontodel.KONTODEL,
    };
    const kontodel = 'KONTODEL_' + rawKontodel.ORDNINGS_NR;

    kontodelarMap[kontodel]?.kontodelar.push(kontodelValue);
  });

  return sortKontodelar(kontodelarMap);
};

const EkonomiskAnalysKontodelarQuery: React.FC<IProps> = props => {
  const kodplanContext = useContext(KodplanContext);
  const sideMenuContext = useContext(EkonomiskAnalysSideMenuContext);
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const ekonomiskAnalysContext = useContext(EkonomiskAnalysContext);

  const newAnalysFromDate = sideMenuContext.newAnalysSettings.dates.from;
  const analysDateString =
    datePartsToCompletedDateString({
      year: newAnalysFromDate.year,
      month: newAnalysFromDate.month,
      day: newAnalysFromDate.day,
    }) || getTodaysDate();

  const queryResult = useHandledQuery<IData, IVariables>(
    query,
    {
      variables: {
        dbName: activeDatabaseContext.activeDatabase
          ? activeDatabaseContext.activeDatabase
          : '',
        kodplanId: kodplanContext.activeKodplan
          ? kodplanContext.activeKodplan.id
          : '',
      },
      skip: kodplanContext.activeKodplan == null,
    },
    { loadingComponent: props.loadingComponent ? props.loadingComponent : null }
  );

  if (!queryResult.data && props.loadingComponent === null) {
    return props.children({});
  } else if (!queryResult.data) {
    return queryResult.renderComponent;
  }
  const data = queryResult.data;

  const activeKodplan = kodplanContext.activeKodplan;
  if (
    !data.db ||
    !data.db.EKKontodel_All ||
    !data.db.EKKontoplan_All ||
    !activeKodplan
  ) {
    return props.children({});
  }

  const usedKodplan = data.db.EKKontoplan_All.find(
    kontoplan => kontoplan.id === activeKodplan.id
  );

  if (
    !usedKodplan ||
    !usedKodplan._Associations ||
    !usedKodplan._Associations.Kontodelsdef
  ) {
    Logger.warn(Error('Could not find used kodplan'));
    return props.children({});
  }

  const activeKoddelar = getActiveKoddelar(
    data.db.EKKontodel_All,
    analysDateString
  );

  let result: IKontodelar = getKontodelarValues(
    activeKoddelar,
    usedKodplan._Associations.Kontodelsdef
  );

  // Filtrera ut koddelar som inte finns inom kodbehörighet och grundurval om det finns
  let urvalList: IUIUrval[] = [];
  if (ekonomiskAnalysContext.kodbehorighetsUrval) {
    urvalList.push(ekonomiskAnalysContext.kodbehorighetsUrval);
  }
  if (ekonomiskAnalysContext.grundurvalUrval) {
    urvalList.push(ekonomiskAnalysContext.grundurvalUrval);
  }
  result = filterKoddelar(result, urvalList);

  return props.children(result);
};

export default EkonomiskAnalysKontodelarQuery;
